import { DependencyList } from 'react';
import { useCallback } from 'react';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAlive } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { uiAppLogger } from 'apps/sigma2-ui/src/lib/logging';

/**
 * Wrap and cache an async function, similarly to `useCallback`.
 *
 * The wrapped function will be called with an `isAlive` function as its first
 * argument. This function can be used to check if the component is still mounted
 * and therefore if its dependencies are safe to use.
 *
 * Reading your dependencies within the callback is generally safe. Writing to them
 * and / or calling them however is not. As a general rule, every time you try to
 * modify / call a dependency after having awaited on an async function, you should
 * use the `isAlive` function argument to determine if it's safe to do so.
 *
 * Sample usage:
 * const cb = useAsyncCallback(async (isAlive, arg) => {
 *   if (isAlive())
 *     setState(someState);
 *
 *   const x = await doSomethingAsync(arg);
 *
 *   if (isAlive())
 *     setState(x);
 * }, [setState]);
 */
export function useAsyncCallback<Args extends any[], R = any>(
  callback: (isAlive: IsAliveFn, ...args: Args) => Promise<R>,
  deps: DependencyList,
): (...args: Args) => void {
  const isAlive = useAlive();

  return useCallback(
    (...args: Args): void => {
      if (!isAlive()) return;

      callback(isAlive, ...args).catch(uiAppLogger.error);
    },
    // eslint‑disable‑next‑line react-hooks/exhaustive-deps
    [...deps, isAlive],
  );
}
